/**
 * Referral helpers — pure constants and rules for the referral card type.
 *
 * A referral is a card for a prospect someone sent to the practice. It is a
 * SEPARATE record type from files, preparation requests and review planner
 * rows, with its own small pipeline (inbox → in_progress → paused /
 * completed). Only the Adviser works referral cards.
 *
 * STATS SEPARATION (CLAUDE.md §10 / §21.1 / §27.6 / §34.7): this module
 * feeds nothing into dashboardStats.js, bonusCalculator.js, bonusClock.js,
 * slaCalculator.js, prepRequests.js or reviewPlanner.js, and imports nothing
 * from them except the pure `isInSelectedMonth` date filter — the SAME
 * shared filter prepRequests.js uses for its Completed column (§27.4:
 * reuse it, never a parallel implementation). Referral statistics (a later
 * step) live in their own utility and never touch file, prep or review
 * numbers.
 *
 * NO CONTACT DETAILS: nothing here, and nothing in the referrals tables,
 * holds a phone number, email address, ID number or address for the prospect
 * or the referrer (POPIA minimality). Contact details stay in the Adviser's
 * phone and mailbox; the app records only that the follow-up is being done.
 */

import { isInSelectedMonth } from './dashboardStats';
import { getTodaySASTDateString } from './dateHelpers';

// ── Vocabulary (must match the CHECK constraints in migration 28) ───────────

export const REFERRAL_STATUSES = ['inbox', 'in_progress', 'paused', 'completed'];

export const REFERRAL_STATUS_LABELS = {
  inbox: 'Inbox',
  in_progress: 'In Progress',
  paused: 'Paused',
  completed: 'Completed',
};

export const REFERRAL_CHANNELS = ['phone', 'whatsapp_sms', 'email', 'in_person'];

export const REFERRAL_CHANNEL_LABELS = {
  phone: 'Phone',
  whatsapp_sms: 'WhatsApp / SMS',
  email: 'Email',
  in_person: 'In person',
};

export const REFERRAL_OUTCOMES = ['converted', 'declined', 'no_response'];

export const REFERRAL_OUTCOME_LABELS = {
  converted: 'Converted',
  declined: 'Declined',
  no_response: 'No response',
};

export const REFERRER_TYPES = ['client', 'professional', 'other'];

export const REFERRER_TYPE_LABELS = {
  client: 'Client',
  professional: 'Professional',
  other: 'Other',
};

/**
 * Default follow-up horizon: a new referral's next action falls two SA
 * business days after the date it was received (weekends and public
 * holidays skipped via businessDays.addBusinessDays — never re-implemented).
 */
export const REFERRAL_NEXT_ACTION_BUSINESS_DAYS = 2;

// ── Transitions ─────────────────────────────────────────────────────────────

/**
 * The ONLY allowed status moves. Anything not listed here is refused by
 * referralsApi.updateReferralStatus (and by the pause / resume / complete
 * helpers, which each check their own precondition):
 *
 *   inbox       → in_progress
 *   in_progress → paused
 *   in_progress → completed
 *   paused      → in_progress
 *
 * Completed is terminal. There is no inbox → paused, no paused → completed,
 * and no move back to inbox.
 */
export const REFERRAL_TRANSITIONS = {
  inbox: ['in_progress'],
  in_progress: ['paused', 'completed'],
  paused: ['in_progress'],
  completed: [],
};

/**
 * True when `from` → `to` is an allowed referral transition.
 */
export function canTransitionReferral(from, to) {
  const allowed = REFERRAL_TRANSITIONS[from];
  return Array.isArray(allowed) && allowed.includes(to);
}

// ── Pipeline placement ──────────────────────────────────────────────────────

/**
 * Referral status → shared-pipeline column key (CLAUDE.md §7.1: the first
 * column's stored key is 'submitted', displayed as Inbox). Referral cards
 * appear ONLY in these four columns — never in Awaiting Signature or
 * Signed & Sent to New Business. Used by the board in a later step; it is
 * defined here so the placement rule has exactly one home.
 */
export const REFERRAL_PIPELINE_COLUMNS = {
  inbox: 'submitted',
  in_progress: 'in_progress',
  paused: 'paused',
  completed: 'completed',
};

// ── Derived state (render-time, never persisted) ────────────────────────────

/**
 * True when the referral's next action date has passed (strictly before
 * today, SAST) and the referral is not completed. Applies to inbox,
 * in_progress AND paused cards — for a paused card next_action_on is the
 * resume date, so "overdue" there means "resume date reached".
 */
export function isReferralOverdue(referral, now = new Date()) {
  if (!referral || referral.pipelineStatus === 'completed') return false;
  if (!referral.nextActionOn) return false;
  return referral.nextActionOn < getTodaySASTDateString(now);
}

/**
 * True when the referral is paused and its resume date is today or earlier
 * (SAST) — the cue that it should be picked up again.
 */
export function isReferralResumeDue(referral, now = new Date()) {
  if (!referral || referral.pipelineStatus !== 'paused') return false;
  if (!referral.nextActionOn) return false;
  return referral.nextActionOn <= getTodaySASTDateString(now);
}

/**
 * True when the referral is completed with an outcome and the referrer has
 * not yet been thanked / updated (feedback_given_at is null).
 */
export function isReferralFeedbackOwed(referral) {
  if (!referral || referral.pipelineStatus !== 'completed') return false;
  return !!referral.outcome && !referral.feedbackGivenAt;
}

/**
 * Whole calendar days by which the referral's next action is overdue (SAST),
 * 0 when not overdue. Render-time only.
 */
export function referralDaysOverdue(referral, now = new Date()) {
  if (!isReferralOverdue(referral, now)) return 0;
  const [y, m, d] = referral.nextActionOn.split('-').map(Number);
  const [ty, tm, td] = getTodaySASTDateString(now).split('-').map(Number);
  const due = Date.UTC(y, m - 1, d);
  const today = Date.UTC(ty, tm - 1, td);
  return Math.max(0, Math.round((today - due) / 86400000));
}

// ── Pipeline column assembly ────────────────────────────────────────────────

/**
 * Referral cards for one pipeline column, in display order. Mirrors
 * getPrepsForColumn (prepRequests.js):
 *   - Inbox / In Progress / Paused: every referral in that status, ordered
 *     by next_action_on ascending (most pressing first; missing dates last),
 *     then by created_at.
 *   - Completed: only those whose completed_at falls in the selected month
 *     (the SAME month rollover files and preps use), newest first.
 * Any other column key (Awaiting Signature, Signed & Sent to New Business)
 * returns [] — referral cards never appear there.
 *
 * Test records are included: they always render on the board (§33.5).
 * DISPLAY ONLY — nothing here is a statistic.
 */
export function getReferralsForColumn(referrals, columnKey, filterMonth) {
  const list = Array.isArray(referrals) ? referrals : [];
  const statuses = Object.keys(REFERRAL_PIPELINE_COLUMNS).filter(
    (status) => REFERRAL_PIPELINE_COLUMNS[status] === columnKey
  );
  if (statuses.length === 0) return [];

  if (columnKey === 'completed') {
    return list
      .filter((r) => r.pipelineStatus === 'completed' && isInSelectedMonth(r.completedAt, filterMonth))
      .sort((a, b) => {
        const at = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bt = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        if (bt !== at) return bt - at;
        return (b.id || '').localeCompare(a.id || '');
      });
  }

  return list
    .filter((r) => statuses.includes(r.pipelineStatus))
    .sort((a, b) => {
      const an = a.nextActionOn || '9999-12-31';
      const bn = b.nextActionOn || '9999-12-31';
      if (an !== bn) return an < bn ? -1 : 1;
      const ac = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bc = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return ac - bc;
    });
}

// ── Needs-attention grouping (render-time, never persisted) ─────────────────

/**
 * The three groups the Adviser dashboard's needs-attention strip lists:
 *   overdue      — inbox / in_progress with next_action_on before today (SAST)
 *   resumeDue    — paused with next_action_on on or before today
 *   feedbackOwed — completed with an outcome and feedback_given_at null
 * Each group is ordered most-pressing first (earliest date first). This is a
 * work list, not a statistic: test records are INCLUDED, exactly as they
 * render on the board (§33.5) — the strip marks them with their TEST flag.
 */
export function computeReferralAttention(referrals, now = new Date()) {
  const list = Array.isArray(referrals) ? referrals : [];
  const byDate = (key) => (a, b) => String(a[key] || '').localeCompare(String(b[key] || ''));
  const overdue = list
    .filter((r) => (r.pipelineStatus === 'inbox' || r.pipelineStatus === 'in_progress') && isReferralOverdue(r, now))
    .sort(byDate('nextActionOn'));
  const resumeDue = list.filter((r) => isReferralResumeDue(r, now)).sort(byDate('nextActionOn'));
  const feedbackOwed = list.filter((r) => isReferralFeedbackOwed(r)).sort(byDate('completedAt'));
  return { overdue, resumeDue, feedbackOwed };
}
